import { useState, useEffect } from 'react';
import type { PartyInfo, VendorInfo } from '@acitra/shared';
import { useDraggable } from '../hooks/useDraggable';

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PartyInfo | VendorInfo) => Promise<void>;
  initialData?: PartyInfo | VendorInfo | null;
  title: string;
  showContactInfo?: boolean;
}

export function AddressModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  title,
  showContactInfo = false,
}: AddressModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draggable modal
  const { position, isDragging, handleMouseDown, reset: resetPosition } = useDraggable();

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '');
      if (showContactInfo && 'email' in (initialData || {})) {
        setEmail((initialData as VendorInfo)?.email || '');
      } else {
        setEmail('');
      }
      if (showContactInfo && 'phone' in (initialData || {})) {
        setPhone((initialData as VendorInfo)?.phone || '');
      } else {
        setPhone('');
      }
      setLine1(initialData?.address?.line1 || '');
      setLine2(initialData?.address?.line2 || '');
      setCity(initialData?.address?.city || '');
      setState(initialData?.address?.state || '');
      setPostalCode(initialData?.address?.postalCode || '');
      setCountry(initialData?.address?.country || '');
      setError(null);
      resetPosition();
    }
  }, [isOpen, initialData, showContactInfo, resetPosition]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const address = {
        line1,
        line2,
        city,
        state,
        postalCode,
        country,
      };

      if (showContactInfo) {
        const vendorData: VendorInfo = {
          name,
          address,
          email: email || undefined,
          phone: phone || undefined,
        };
        await onSave(vendorData);
      } else {
        const partyData: PartyInfo = {
          name,
          address,
        };
        await onSave(partyData);
      }

      onClose();
    } catch (err) {
      console.error('Failed to save address:', err);
      setError(err instanceof Error ? err.message : 'Failed to save address');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-15 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl w-full max-w-md"
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
          }}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-6 py-4 border-b border-gray-200 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 text-gray-400" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="4" cy="3" r="1.5" />
                <circle cx="12" cy="3" r="1.5" />
                <circle cx="4" cy="8" r="1.5" />
                <circle cx="12" cy="8" r="1.5" />
                <circle cx="4" cy="13" r="1.5" />
                <circle cx="12" cy="13" r="1.5" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              onMouseDown={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-gray-500 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form id="address-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Name */}
            <div>
              <label
                htmlFor="company-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Name
              </label>
              <input
                type="text"
                id="company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Company Name"
              />
            </div>

            {/* Address Lines */}
            <div>
              <label
                htmlFor="address-line1"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Address
              </label>
              <input
                type="text"
                id="address-line1"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm mb-2"
                placeholder="Street Address"
              />
              <input
                type="text"
                id="address-line2"
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Suite, Unit, Building, etc."
              />
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-3">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
              <div className="col-span-1">
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
              <div className="col-span-2">
                <label
                  htmlFor="postal-code"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Zip
                </label>
                <input
                  type="text"
                  id="postal-code"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Country Code (e.g. US)"
              />
            </div>

            {/* Contact Info (Vendor only) */}
            {showContactInfo && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="vendor@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="address-form"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
